<template>
    <div class="container-fluid">
        <!-- Custom Tab Navigation -->
        <ul class="nav nav-tabs custom-tabs" role="tablist">
            <li class="nav-item" v-for="(value, key, index) in userData" :key="index">
                <a 
                    class="nav-link" 
                    :class="{ active: selectedTab === index }"
                    @click="selectedTab = index"
                    href="#"
                    role="tab"
                >
                    {{ formatTabName(key) }}
                </a>
            </li>
        </ul>
        <div class="tab-content mt-3">
            <div 
                v-for="(value, key, index) in userData" 
                :key="index"
                class="tab-pane"
                :class="{ active: selectedTab === index }"
            >
                <div v-if="Array.isArray(value) && value.length">
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <div class="input-group">
                                <input 
                                    v-model="searchQuery" 
                                    type="text" 
                                    class="form-control" 
                                    placeholder="Search..."
                                />
                            </div>
                        </div>
                        <div class="col-md-6 text-right">
                            <select v-model="perPage" class="form-control w-50 d-inline-block">
                                <option v-for="option in pageSizeOptions" :key="option" :value="option">
                                    {{ option }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div style="width:90vw;overflow-x: scroll;">
                        <table class="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th 
                                        v-for="header in getHeaders(key, value)" 
                                        :key="header.key"
                                        class="sortable-header"
                                        :class="header.thClass"
                                        :style="header.thStyle"
                                        @click="header.sortable ? sortTable(header.key) : null"
                                    >
                                        {{ header.label }}
                                        <span v-if="header.sortable && sortBy === header.key">
                                            <i :class="sortDesc ? 'fas fa-sort-down' : 'fas fa-sort-up'"></i>
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr 
                                    v-for="(item, rowIndex) in paginatedAndFilteredData(key, value)" 
                                    :key="rowIndex"
                                    @click="openModal(key, item)"
                                    :class="getRowClass(key, item)"
                                    style="cursor: pointer;"
                                >
                                    <td v-for="header in getHeaders(key, value)" :key="header.key">
                                        <button 
                                            v-if="header.key === 'reset'" 
                                            class="btn btn-danger btn-sm"
                                            @click.stop="resetUserDevice(item)"
                                        >
                                            Reset User Device
                                        </button>
                                        <button 
                                            v-else-if="header.key === 'enrollment'" 
                                            class="btn btn-danger btn-sm"
                                            @click.stop="enrolledUser(item)"
                                        >
                                            Enroll Course
                                        </button>
                                        <a v-else-if="header.key === 'viewedfromip'" :href="'https://ip-api.com/' + item.viewedFromIP" target="_blank" class="btn btn-primary btn-sm">View Country</a>
                                        <span v-else>{{ item[header.key] }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <nav aria-label="Table pagination">
                        <ul class="pagination pagination-sm justify-content-center">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <a class="page-link" @click="currentPage = Math.max(1, currentPage - 1)" href="#">
                                    Previous
                                </a>
                            </li>
                            <li 
                                v-for="page in totalPages" 
                                :key="page"
                                class="page-item"
                                :class="{ active: currentPage === page }"
                            >
                                <a class="page-link" @click="currentPage = page" href="#">{{ page }}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <a class="page-link" @click="currentPage = Math.min(totalPages, currentPage + 1)" href="#">
                                    Next
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>

                <div v-else-if="value && typeof value === 'object' && Object.keys(value).length">
                    <pre>{{ value }}</pre>
                </div>

                <div v-else>
                    <p class="text-center text-muted">No Data Available</p>
                </div>
            </div>
        </div>
        <UserLogsModal 
            :user="selectedUser" 
            :isOpen="localModalState" 
            @update:isOpen="syncModalState" 
            @close="closeModal" 
            :tenantId="tenantId" 
        />
        <UserSubscriptionLogsModal 
            :user="selectedUser" 
            :isOpen="userSubscriptionlocalModalState" 
            @update:isOpen="usersubscriptionsyncModalState" 
            @close="closeUserSubscriptionModal" 
            :tenantId="tenantId" 
        />
    </div>
</template>

<script>
import UserLogsModal from "./UserLogsModal.vue";
import UserSubscriptionLogsModal from "./UserSubscriptionLogsModal.vue";
import {ResetUserDevices, EnrollInCourse} from "../../Helper/UserApiHelper";

export default {
    name: "UserSupportDataTable",
    components: { UserLogsModal, UserSubscriptionLogsModal },
    props: {
        userData: {
            type: Object,
            required: true
        },
        tenantId: String
    },
    data() {
        return {
            searchQuery: "",
            currentPage: 1,
            perPage: 10, 
            pageSizeOptions: [5, 10, 20, 50, 100],
            localModalState: false,
            selectedUser: null,
            selectedTab: 0,
            userSubscriptionlocalModalState: false,
            sortBy: null,
            sortDesc: false
        };
    },
    computed: {
        activeTabKey() {
            return Object.keys(this.userData)[this.selectedTab] || "";
        },
        totalPages() {
            const activeData = Object.values(this.userData)[this.selectedTab];
            if (!Array.isArray(activeData)) return 1;
            const filteredData = this.filterData(activeData);
            return Math.ceil(filteredData.length / this.perPage);
        }
    },
    methods: {
        formatTabName(key) {
            return key.replace(/([A-Z])/g, " $1")
                      .replace(/[_-]/g, " ")
                      .replace(/\b\w/g, char => char.toUpperCase())
                      .trim();
        },
        getHeaders(key, items) {
            if (!Array.isArray(items) || items.length === 0) return [];
            let headers = Object.keys(items[0]).map(key => ({
                key,
                label: this.formatTabName(key),
                sortable: true,
                thClass: 'sortable-header',
                thStyle: {
                    whiteSpace: 'nowrap',
                    minWidth: 'max-content'
                }
            }));
            
            if(key === "userData"){
                headers.push({ 
                    key: "reset", 
                    label: "Reset User Device", 
                    sortable: false,
                    thClass: 'text-center',
                    thStyle: {whiteSpace: 'nowrap', minWidth: 'max-content'}
                });
            }
            if(key === 'userSubscriptionInfo'){
                headers.push({
                    key: "enrollment",
                    label: "Enrolled Course",
                    sortable: false,
                    thClass: 'text-center',
                    thStyle: {whiteSpace: 'nowrap', minWidth: 'max-content'}
                });
            }
            if(key.toLowerCase() === 'recentcontentinfo'){
                headers.push({
                    key: "viewedfromip",
                    label: "View Country",
                    sortable: false,
                    thClass: 'text-center',
                    thStyle: {whiteSpace: 'nowrap', minWidth: 'max-content'}
                });
            }
            return headers;
        },
        getRowClass(tabKey, item) {
            if (item.isDeleted === true || item.isUserDeleted === true || item.isUserSubscriptionDeleted === true) {
                return 'table-danger'; // Bootstrap class for red background
            }
            return '';
        },
        filterData(data) {
            if (!this.searchQuery) return data;
            return data.filter(item => {
                return Object.values(item).some(value => 
                    String(value).toLowerCase().includes(this.searchQuery.toLowerCase())
                );
            });
        },
        sortTable(key) {
            if (this.sortBy === key) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = key;
                this.sortDesc = false;
            }
        },
        paginatedAndFilteredData(key, data) {
            let filteredData = this.filterData(data);
            
            // Apply sorting
            if (this.sortBy) {
                filteredData.sort((a, b) => {
                    let aVal = a[this.sortBy];
                    let bVal = b[this.sortBy];
                    
                    if (aVal < bVal) return this.sortDesc ? 1 : -1;
                    if (aVal > bVal) return this.sortDesc ? -1 : 1;
                    return 0;
                });
            }
            
            // Apply pagination
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return filteredData.slice(start, end);
        },
        pageChanged(page) {
            this.currentPage = page;
        },
        openModal(tabKey, row) {
            console.log(tabKey);
            console.log(row);
            if (tabKey === "userData") { 
                this.selectedUser = row;
                this.localModalState = true;
            } else if (tabKey === "userSubscriptionInfo") {
                this.selectedUser = row;
                this.userSubscriptionlocalModalState = true;
            }
        },
        closeModal() {
            this.localModalState = false;
        },
        closeUserSubscriptionModal() {
            this.userSubscriptionlocalModalState = false;
        },
        syncModalState(newState) {
            this.localModalState = newState;
        },
        usersubscriptionsyncModalState(newState) {
            this.userSubscriptionlocalModalState = newState;
        },
        async resetUserDevice(user) {
            if (!user.email) {
                alert("User email not found!");
                return;
            }

            if (!confirm("Are you sure you want to reset the user device?")) return;

            try {
                let resetData = {
                    UserEmail: user.email,
                    TenantID: this.tenantId,
                    Platform_Optional: null
                };
                let response = await ResetUserDevices(resetData);
                console.log(response);
                if (response?.message) {
                    this.$toasted.success(response.message, {
                        theme: "outline",
                        position: "bottom-center",
                        duration: 3000,
                    });
                } 
            } catch (error) {
                console.error("Error resetting device:", error);
                this.$toasted.error("Error resetting device.", {
                    icon: "exclamation-triangle",
                    theme: "outline",
                    position: "bottom-center",
                    singleton: true,
                    duration: 3000,
                });
            }
        },
        async enrolledUser(productVariant){
            if(!productVariant.id){
                alert("Product Variant ID not found!");
                return;
            }
            if (!confirm("Are you sure you want to enroll in this course?")) return;
            try{
                let enrollCourseData = {
                    productVariantID: productVariant.id,
                    tenantId: this.tenantId
                };
                let response = await EnrollInCourse(enrollCourseData);
                if (response?.message) {
                    this.$toasted.success(response.message, {
                        theme: "outline",
                        position: "bottom-center",
                        duration: 3000,
                    });
                } 
            }catch(err){
                console.error(err);
                this.$toasted.error("Error in enrolling course.", {
                    icon: "exclamation-triangle",
                    theme: "outline",
                    position: "bottom-center",
                    singleton: true,
                    duration: 3000,
                });
            }
        }
    },
    watch: {
        selectedTab() {
            // Reset pagination when switching tabs
            this.currentPage = 1;
            this.sortBy = null;
            this.sortDesc = false;
        }
    }
};
</script>

<style scoped>
.custom-tabs {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: thin;
    scrollbar-color: #bbb transparent;
}

.custom-tabs::-webkit-scrollbar {
    height: 5px;
}

.custom-tabs::-webkit-scrollbar-thumb {
    background: #bbb;
    border-radius: 10px;
}

.sortable-header {
    cursor: pointer;
    user-select: none;
    white-space: nowrap !important;
}

.sortable-header:hover {
    background-color: #f8f9fa;
}

.table th {
    white-space: nowrap !important;
    vertical-align: middle;
}

.table th,
.table td {
    padding: 0.75rem 0.5rem;
}

/* Red background for deleted users */
.table-danger {
    background-color: #f8d7da !important;
}

.table-danger:hover {
    background-color: #f5c6cb !important;
}

.nav-link {
    cursor: pointer;
}
</style>